import { forwardRef } from 'react';
import styled from 'styled-components';
import { color, space } from 'styled-system';

import type { Colors, Elevation, Spacing } from '../StyledSystemTheme';

type CommonProps = Readonly<{
  className?: string;
  children: JSX.Element | JSX.Element[];
  dataTest?: string;
  backgroundColor?: Colors;
  elevation?: Elevation | null;
  elevationActive?: Elevation;
  padding?: Spacing;
  paddingX?: Spacing;
  paddingY?: Spacing;
}>;
type Props =
  | Readonly<
      CommonProps & {
        href: string;
        onClick?: () => void | Promise<void>;
      }
    >
  | Readonly<
      CommonProps & {
        onClick?: () => void | Promise<void>;
      }
    >;

/**
 * Tile is an interactive component, or a clickable card.
 * If you want to use this as a div, use a card instead.
 */

const Tile = (
  {
    dataTest,
    backgroundColor = 'paletteWhite',
    elevation = 'elevationFixedBoxShadow',
    elevationActive = 'elevationLevel2BoxShadow',
    padding = '600',
    children,
    ...rest
  }: Props,
  ref,
): JSX.Element => {
  const href = 'href' in rest ? rest.href : null;
  const { onClick } = rest;
  return (
    <StyledTile
      elevation={elevation}
      elevationActive={elevationActive}
      backgroundColor={backgroundColor}
      data-test={dataTest}
      padding={padding}
      ref={ref}
      {...rest}
      as={href == null && onClick != null ? 'button' : null}
    >
      {children}
    </StyledTile>
  );
};

type TileProps = {
  className?: string;
  elevationActive: Elevation;
  elevation: Elevation;
  as: string;
  backgroundColor: Colors;
  padding: Spacing;
};

const StyledTile = styled.a<TileProps>`
  position: relative;
  border: none;
  display: block;
  width: 100%;
  text-align: start;
  box-shadow: ${({ theme }) => theme.orbit.elevationFixedBoxShadow};
  cursor: ${({ href, onClick }) => (href != null || onClick != null) && 'pointer'};
  text-decoration: none;
  transition: box-shadow 0.3s ease-out;
  height: 100%;
  border-radius: ${({ theme }) => theme.orbit.borderRadius100};
  &:hover {
    ${({ theme, elevation }) =>
      elevation && `box-shadow: ${theme.orbit.elevationLevel2BoxShadow}; outline: none;`}
  }
  &:focus {
    ${({ theme, elevationActive }) =>
      elevationActive && `box-shadow: ${theme.orbit.elevationLevel2BoxShadow}; outline: none;`}
  }
  ${color}
  ${space}
`;
export default forwardRef<HTMLAnchorElement, Props>(Tile);
